import React from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { Book } from '../../books/types';
import { bookIsActiveOnDate, formatShortDate, getBookActiveEnd, getCurrentPacificDate } from '../../books/utils';

interface BooksCalendarProps {
  books: Book[];
}

const startOfMonth = (value: Date): Date => new Date(value.getFullYear(), value.getMonth(), 1);

const buildCalendarDays = (month: Date): Date[] => {
  const first = startOfMonth(month);
  const firstWeekday = first.getDay();
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - firstWeekday);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
};

const buildCalendarWeeks = (month: Date): Date[][] => {
  const days = buildCalendarDays(month);
  return Array.from({ length: days.length / 7 }, (_, index) => days.slice(index * 7, index * 7 + 7));
};

const isSameCalendarDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const parseIsoDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
};

const BooksCalendar: React.FC<BooksCalendarProps> = ({ books }) => {
  const today = React.useMemo(() => parseIsoDate(getCurrentPacificDate()), []);
  const [month, setMonth] = React.useState(() => startOfMonth(today));
  const weeks = React.useMemo(() => buildCalendarWeeks(month), [month]);

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        avatar={<CalendarMonthIcon />}
        title="Reading calendar"
        action={
          <Stack direction="row" spacing={0.5}>
            <IconButton onClick={() => setMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
        subheader={month.toLocaleDateString([], { month: 'long', year: 'numeric' })}
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: 1,
            mb: 1,
          }}
        >
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
            <Typography key={label} variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {label}
            </Typography>
          ))}
        </Box>
        <Stack spacing={1}>
          {weeks.map((week, weekIndex) => {
            const weekBooks = books
              .filter((book) => week.some((day) => bookIsActiveOnDate(book, day)))
              .sort((left, right) => (
                left.start_date.localeCompare(right.start_date) ||
                getBookActiveEnd(left).localeCompare(getBookActiveEnd(right)) ||
                left.title.localeCompare(right.title)
              ));

            return (
              <Box
                key={week[0]?.toISOString() ?? `week-${weekIndex}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                  gap: 0,
                }}
              >
                {week.map((day, dayIndex) => {
                  const inMonth = day.getMonth() === month.getMonth();
                  const isToday = isSameCalendarDay(day, today);
                  return (
                    <Box
                      key={day.toISOString()}
                      sx={{
                        minHeight: 44 + weekBooks.length * 22,
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderRight: '1px solid',
                        borderLeft: dayIndex === 0 ? '1px solid' : 0,
                        borderColor: inMonth ? 'divider' : 'transparent',
                        bgcolor: inMonth ? 'background.paper' : 'action.hover',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        opacity: inMonth ? 1 : 0.6,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          border: isToday ? '2px solid' : '2px solid transparent',
                          borderColor: isToday ? 'primary.main' : 'transparent',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          color: isToday ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {day.getDate()}
                      </Typography>
                      <Stack spacing={0.5}>
                        {weekBooks.length === 0 ? <Box sx={{ height: 20 }} /> : null}
                        {weekBooks.map((book) => {
                          const isActive = bookIsActiveOnDate(book, day);
                          const activePreviousDay = dayIndex > 0 ? bookIsActiveOnDate(book, week[dayIndex - 1]) : false;
                          const activeNextDay = dayIndex < week.length - 1 ? bookIsActiveOnDate(book, week[dayIndex + 1]) : false;
                          const segmentStart = isActive && !activePreviousDay;
                          const segmentEnd = isActive && !activeNextDay;
                          const borderColor = book.color;

                          return (
                            <Tooltip
                              key={`${book.id}-${day.toISOString()}`}
                              title={`${book.title}: ${formatShortDate(book.start_date)} - ${formatShortDate(
                                getBookActiveEnd(book)
                              )}`}
                              arrow
                            >
                              <Box
                                sx={{
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  px: segmentStart ? 0.75 : 0,
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  bgcolor: isActive ? alpha(borderColor, 0.14) : 'transparent',
                                  borderTop: isActive ? '1px solid' : '1px solid transparent',
                                  borderBottom: isActive ? '1px solid' : '1px solid transparent',
                                  borderLeft: segmentStart ? '1px solid' : '1px solid transparent',
                                  borderRight: segmentEnd ? '1px solid' : '1px solid transparent',
                                  borderColor: isActive ? borderColor : 'transparent',
                                  borderTopLeftRadius: segmentStart ? 999 : 0,
                                  borderBottomLeftRadius: segmentStart ? 999 : 0,
                                  borderTopRightRadius: segmentEnd ? 999 : 0,
                                  borderBottomRightRadius: segmentEnd ? 999 : 0,
                                }}
                              >
                                {segmentStart ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      color: 'text.primary',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {book.title}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BooksCalendar;
